import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import api, { API } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Progress } from "../../components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { STATUS_LABELS } from "../../lib/options";
import { LogOut, FileText, Download, Pencil, KeyRound, ShieldAlert, DownloadCloud, Trash2, Briefcase } from "lucide-react";

const STATUS_COLOR = {
  draft: "bg-slate-100 text-slate-600", submitted: "bg-[#C79A3B]/15 text-[#8a6a1e]",
  under_review: "bg-blue-100 text-blue-700", shortlisted: "bg-[#2E7D32]/10 text-[#2E7D32]",
  interview: "bg-purple-100 text-purple-700", second_interview: "bg-purple-100 text-purple-700",
  offer: "bg-[#2E7D32]/15 text-[#2E7D32]", not_progressing: "bg-red-100 text-red-600", withdrawn: "bg-slate-100 text-slate-500",
};

export default function CandidateDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [apps, setApps] = useState([]);
  const [profile, setProfile] = useState({});
  const [cv, setCv] = useState(null);
  const [pw, setPw] = useState({ current_password: "", new_password: "" });

  const load = async () => {
    const [a, p, c] = await Promise.all([api.get("/candidate/applications"), api.get("/candidate/profile"), api.get("/candidate/cv")]);
    setApps(a.data); setProfile(p.data || {}); setCv(c.data && c.data.id ? c.data : null);
  };
  useEffect(() => { load(); }, []);

  const completion = () => {
    const fields = ["first_name","last_name","phone","county","country_residence","resident_ireland","right_to_work","driving_licence","willing_travel","current_job_title","years_total_sales","channels","network_relationships","commercial_skills","highest_qualification"];
    const filled = fields.filter((f) => { const v = profile[f]; return v !== undefined && v !== "" && v !== null && !(Array.isArray(v) && v.length === 0); });
    return Math.round(filled.length / fields.length * 100);
  };

  const withdraw = async (id) => { await api.post(`/candidate/applications/${id}/withdraw`); toast.success("Application withdrawn"); load(); };
  const changePw = async () => {
    try { await api.post("/candidate/change-password", pw); toast.success("Password updated"); setPw({ current_password: "", new_password: "" }); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const downloadData = async () => {
    const { data } = await api.get("/candidate/my-data");
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = url; a.download = "kera-my-data.json"; a.click(); URL.revokeObjectURL(url);
  };
  const requestDeletion = async () => { await api.post("/candidate/delete-request"); toast.success("Deletion request received"); };

  return (
    <div className="min-h-screen bg-[#F7F0E2]">
      <header className="bg-[#101820] text-[#FAF6EE] border-b border-[#C79A3B]/25">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/kera-logo.webp" alt="KERA" className="h-8 w-auto" />
          </Link>
          <Button data-testid="logout-button" variant="ghost" onClick={() => { logout(); navigate("/"); }} className="text-[#C79A3B] hover:bg-white/5"><LogOut className="h-4 w-4 mr-1" /> Log out</Button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-8 space-y-6">
        <div>
          <h1 className="font-heading text-3xl font-extrabold text-[#101820]">Welcome, {profile.first_name || user?.username || "there"}</h1>
          <p className="text-muted-foreground">Track your applications and keep your profile up to date.</p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          <div className="bg-white rounded-xl border border-[#C79A3B]/30 p-6 shadow-md md:col-span-1">
            <h3 className="font-heading font-bold text-[#101820]">Profile Completion</h3>
            <div className="mt-4 flex items-center gap-4">
              <div className="text-3xl font-heading font-extrabold text-[#8a6a1e]">{completion()}%</div>
              <Progress value={completion()} className="h-2 flex-1" />
            </div>
            <Button data-testid="update-profile-button" onClick={() => navigate("/apply/senior-sales-executive-ireland")} className="mt-5 w-full bg-[#101820] text-[#FAF6EE] rounded-full">
              <Pencil className="h-4 w-4 mr-1" /> Update profile
            </Button>
          </div>

          <div className="bg-white rounded-xl border border-[#C79A3B]/30 p-6 shadow-md md:col-span-2">
            <h3 className="font-heading font-bold text-[#101820] mb-2">Your CV</h3>
            {cv ? (
              <div className="flex items-center gap-3 bg-[#FAF6EE] border border-[#C79A3B]/25 rounded-lg p-4">
                <FileText className="h-7 w-7 text-[#C79A3B]" />
                <div className="flex-1 min-w-0"><div className="font-medium text-[#101820] truncate">{cv.original_filename}</div><div className="text-xs text-muted-foreground">{Math.round((cv.size||0)/1024)} KB</div></div>
                <a href={`${API}/files/cv/${cv.id}/download?auth=${localStorage.getItem("kera_token")}`} target="_blank" rel="noreferrer"><Button variant="outline" size="sm" className="border-[#C79A3B]/40"><Download className="h-4 w-4" /></Button></a>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No CV uploaded yet. <button className="text-[#8a6a1e] hover:underline" onClick={() => navigate("/apply/senior-sales-executive-ireland")}>Upload now →</button></p>
            )}
            <div className="flex flex-wrap gap-3 mt-4">
              <Dialog>
                <DialogTrigger asChild><Button data-testid="change-password-button" variant="outline" size="sm" className="border-[#C79A3B]/40"><KeyRound className="h-4 w-4 mr-1" /> Change password</Button></DialogTrigger>
                <DialogContent><DialogHeader><DialogTitle>Change password</DialogTitle></DialogHeader>
                  <div className="space-y-4">
                    <div><Label>Current password</Label><Input type="password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} className="mt-1.5" /></div>
                    <div><Label>New password</Label><Input type="password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} className="mt-1.5" /></div>
                    <Button data-testid="save-password-button" onClick={changePw} className="w-full bg-[#101820] text-[#FAF6EE]">Update password</Button>
                  </div>
                </DialogContent>
              </Dialog>
              <Button data-testid="download-data-button" variant="outline" size="sm" onClick={downloadData} className="border-[#C79A3B]/40"><DownloadCloud className="h-4 w-4 mr-1" /> Download my data</Button>
              <Button data-testid="delete-request-button" variant="outline" size="sm" onClick={requestDeletion} className="border-[#C76A32]/40 text-[#C76A32]"><Trash2 className="h-4 w-4 mr-1" /> Request deletion</Button>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-[#C79A3B]/30 shadow-md overflow-hidden">
          <div className="px-6 py-4 border-b border-[#C79A3B]/15 flex items-center gap-2"><Briefcase className="h-4 w-4 text-[#C79A3B]" /><h3 className="font-heading font-bold text-[#101820]">Your Applications</h3></div>
          {apps.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">You haven't started an application yet. <Link className="text-[#8a6a1e] hover:underline" to="/jobs/senior-sales-executive-ireland">View the open role →</Link></div>
          ) : (
            <div className="divide-y divide-[#C79A3B]/10">
              {apps.map((a) => (
                <div key={a.id} data-testid={`application-row-${a.id}`} className="px-6 py-4 flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
                  <div>
                    <div className="font-medium text-[#101820]">{a.job_title}</div>
                    <div className="text-xs text-muted-foreground font-mono-data">{a.reference || "Draft — not submitted"} · {a.submitted_at ? new Date(a.submitted_at).toLocaleDateString() : "—"}</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`text-xs font-semibold px-3 py-1 rounded-full ${STATUS_COLOR[a.status] || "bg-slate-100"}`}>{STATUS_LABELS[a.status] || a.status}</span>
                    {a.status === "draft" && <Button size="sm" onClick={() => navigate(`/apply/${a.application_type === "internship" ? "sales-marketing-intern-dublin" : "senior-sales-executive-ireland"}`)} className="bg-[#C79A3B] text-[#101820] rounded-full">Continue</Button>}
                    {["submitted","under_review","shortlisted","interview","second_interview"].includes(a.status) &&
                      <Button data-testid={`withdraw-${a.id}`} size="sm" variant="outline" onClick={() => withdraw(a.id)} className="border-[#C76A32]/40 text-[#C76A32] rounded-full">Withdraw</Button>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-start gap-2 text-xs text-muted-foreground bg-white/50 rounded-lg p-4 border border-[#C79A3B]/20">
          <ShieldAlert className="h-4 w-4 text-[#C79A3B] shrink-0 mt-0.5" />
          KERA processes your data for recruitment purposes under GDPR. Internal recruiter scores and notes are never shared with candidates.
        </div>
      </div>
    </div>
  );
}
